import * as XLSX from "xlsx";

/**
 * Lectura y detección de columnas de una nómina de inhabilitados. El Excel se
 * parsea en el navegador (nunca se sube el binario) y sólo se envían al backend
 * las filas ya mapeadas contra los campos del sistema.
 *
 * Se apoya en las mismas ideas que `portalPayrollExcel.ts`: detección
 * automática de la fila de encabezado y sugerencia de match por alias.
 */

export type InhabilitadoFieldKey =
  | "rut"
  | "rutDigito"
  | "nombreCompleto"
  | "nombres"
  | "primerNombre"
  | "segundoNombre"
  | "apellidos"
  | "primerApellido"
  | "segundoApellido"
  | "comentario";

export interface InhabilitadoSystemColumn {
  key: InhabilitadoFieldKey;
  label: string;
  description?: string;
  required?: boolean;
  aliases: string[];
}

export const INHABILITADO_SYSTEM_COLUMNS: InhabilitadoSystemColumn[] = [
  {
    key: "rut",
    label: "RUT",
    description: "Obligatorio. Se valida formato y dígito verificador.",
    required: true,
    aliases: ["rut", "run", "dni", "documento", "cedula", "identificacion"],
  },
  {
    key: "rutDigito",
    label: "Dígito verificador",
    description: "Solo el dígito verificador (ej: K o 9).",
    aliases: [
      "dv",
      "digito verificador",
      "digito",
      "verificador",
      "dígito",
      "dig",
    ],
  },
  {
    key: "nombreCompleto",
    label: "Nombre completo",
    description: "Nombres y apellidos en una sola columna.",
    aliases: [
      "nombre completo",
      "nombres y apellidos",
      "nombre y apellido",
      "nombre candidato",
      "nombre del candidato",
      "nombre postulante",
    ],
  },
  {
    key: "nombres",
    label: "Nombres",
    description: "Primer y segundo nombre juntos.",
    aliases: ["nombres", "nombre", "primer y segundo nombre"],
  },
  {
    key: "primerNombre",
    label: "Primer nombre",
    aliases: ["primer nombre", "nombre 1", "nombre1"],
  },
  {
    key: "segundoNombre",
    label: "Segundo nombre",
    aliases: ["segundo nombre", "nombre 2", "nombre2"],
  },
  {
    key: "apellidos",
    label: "Apellidos",
    description: "Primer y segundo apellido juntos.",
    aliases: ["apellidos", "apellido", "primer y segundo apellido"],
  },
  {
    key: "primerApellido",
    label: "Primer apellido",
    aliases: ["primer apellido", "apellido paterno", "apellido 1", "apellido1"],
  },
  {
    key: "segundoApellido",
    label: "Segundo apellido",
    aliases: ["segundo apellido", "apellido materno", "apellido 2", "apellido2"],
  },
  {
    key: "comentario",
    label: "Comentario",
    description: "Comentario del bloqueo por registro (opcional).",
    aliases: [
      "comentario",
      "comentarios",
      "observacion",
      "observaciones",
      "motivo",
      "glosa",
      "detalle",
    ],
  },
];

/**
 * Arma `nombres` y `apellidos` desde el mapeo, soportando: columna de nombre
 * completo, nombres+apellidos juntos, o cada nombre/apellido por separado.
 * La regla de respaldo para "nombre completo" toma los dos últimos tokens como
 * apellidos (orden chileno: nombre1 nombre2 apellido1 apellido2).
 */
export const resolveRowNames = (
  row: Record<string, string | number | boolean>,
  mapping: Record<string, string>
): { nombres: string; apellidos: string } => {
  const get = (key: InhabilitadoFieldKey): string =>
    mapping[key] ? String(row[mapping[key]] ?? "").trim() : "";

  const primerNombre = get("primerNombre");
  const segundoNombre = get("segundoNombre");
  const nombresCol = get("nombres");
  const primerApellido = get("primerApellido");
  const segundoApellido = get("segundoApellido");
  const apellidosCol = get("apellidos");
  const completo = get("nombreCompleto");

  let nombres = "";
  let apellidos = "";

  if (primerNombre || segundoNombre) {
    nombres = [primerNombre, segundoNombre].filter(Boolean).join(" ");
  } else if (nombresCol) {
    nombres = nombresCol;
  }

  if (primerApellido || segundoApellido) {
    apellidos = [primerApellido, segundoApellido].filter(Boolean).join(" ");
  } else if (apellidosCol) {
    apellidos = apellidosCol;
  }

  if (completo && (!nombres || !apellidos)) {
    const parts = completo.split(/\s+/).filter(Boolean);
    let fullNombres = "";
    let fullApellidos = "";
    if (parts.length >= 4) {
      fullApellidos = parts.slice(-2).join(" ");
      fullNombres = parts.slice(0, -2).join(" ");
    } else if (parts.length === 3) {
      fullNombres = parts[0];
      fullApellidos = parts.slice(1).join(" ");
    } else if (parts.length === 2) {
      fullNombres = parts[0];
      fullApellidos = parts[1];
    } else {
      fullNombres = completo;
    }
    if (!nombres) nombres = fullNombres;
    if (!apellidos) apellidos = fullApellidos;
  }

  return { nombres, apellidos };
};

/**
 * Arma el RUT desde el mapeo. Si el RUT viene dividido en dos columnas
 * (cuerpo + dígito verificador) los concatena como `11222333-K`; si viene en
 * una sola columna lo devuelve tal cual. La presencia de `mapping.rutDigito`
 * indica que el usuario eligió el formato de dos columnas.
 */
export const resolveRowRut = (
  row: Record<string, string | number | boolean>,
  mapping: Record<string, string>
): string => {
  const body = mapping.rut ? String(row[mapping.rut] ?? "").trim() : "";
  if (mapping.rutDigito) {
    const dv = String(row[mapping.rutDigito] ?? "").trim();
    if (!body && !dv) return "";
    return `${body}-${dv}`;
  }
  return body;
};

/** Lee el comentario mapeado desde el Excel (si existe). */
export const resolveRowComment = (
  row: Record<string, string | number | boolean>,
  mapping: Record<string, string>
): string =>
  mapping.comentario ? String(row[mapping.comentario] ?? "").trim() : "";

export interface InhabilitadoSheetData {
  headers: string[];
  rows: Record<string, string | number | boolean>[];
  headerRow: number;
}

export const normalizeInhabilitadoHeader = (value: unknown): string =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const hasCellValue = (value: unknown): boolean =>
  value !== null && value !== undefined && String(value).trim() !== "";

export const findInhabilitadoHeaderRow = (matrix: unknown[][]): number => {
  const candidates = matrix.slice(0, 30);
  const knownHeaders = new Set(
    INHABILITADO_SYSTEM_COLUMNS.flatMap((column) => [
      column.label,
      ...column.aliases,
    ]).map(normalizeInhabilitadoHeader)
  );
  let bestIndex = -1;
  let bestScore = -1;
  candidates.forEach((row, index) => {
    if (!Array.isArray(row)) return;
    const values = row.filter(hasCellValue);
    if (values.length === 0) return;
    const unique = new Set(values.map(normalizeInhabilitadoHeader)).size;
    const textValues = values.filter((value) => typeof value === "string").length;
    const recognizedHeaders = values.filter((value) =>
      knownHeaders.has(normalizeInhabilitadoHeader(value))
    ).length;
    const dataShapedValues = values.filter((value) => {
      const text = String(value).trim();
      return (
        typeof value === "number" ||
        value instanceof Date ||
        /^\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}$/.test(text) ||
        /^\d{6,9}-[0-9kK]$/.test(text.replace(/\./g, "")) ||
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)
      );
    }).length;
    const score =
      recognizedHeaders * 50 +
      values.length * 10 +
      unique +
      textValues -
      dataShapedValues * 20;
    if (score > bestScore) {
      bestIndex = index;
      bestScore = score;
    }
  });
  return bestIndex;
};

const serializeCell = (value: unknown): string | number | boolean => {
  if (value instanceof Date && !Number.isNaN(value.getTime()))
    return value.toISOString();
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const extractSheetAtHeader = (
  workbook: XLSX.WorkBook,
  sheetName: string,
  forcedHeaderIndex?: number
): InhabilitadoSheetData => {
  const worksheet = workbook.Sheets[sheetName];
  if (!worksheet) throw new Error("No se encontró la hoja seleccionada.");
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
    header: 1,
    defval: "",
    raw: true,
    blankrows: false,
  }) as unknown[][];
  const headerIndex = forcedHeaderIndex ?? findInhabilitadoHeaderRow(matrix);
  if (headerIndex < 0)
    throw new Error("No se encontraron columnas en la hoja seleccionada.");

  const seen = new Map<string, number>();
  const headerCells = (matrix[headerIndex] ?? [])
    .map((value, columnIndex) => {
      if (!hasCellValue(value)) return undefined;
      const base = String(value).trim();
      const count = (seen.get(base) ?? 0) + 1;
      seen.set(base, count);
      return { columnIndex, name: count === 1 ? base : `${base} (${count})` };
    })
    .filter(Boolean) as Array<{ columnIndex: number; name: string }>;
  if (headerCells.length === 0)
    throw new Error("No se encontraron columnas en la hoja seleccionada.");

  const rows = matrix
    .slice(headerIndex + 1)
    .map((matrixRow) => {
      const row: Record<string, string | number | boolean> =
        Object.create(null);
      headerCells.forEach(({ columnIndex, name }) => {
        row[name] = serializeCell(matrixRow?.[columnIndex]);
      });
      return row;
    })
    .filter((row) => Object.values(row).some(hasCellValue));

  return {
    headers: headerCells.map(({ name }) => name),
    rows,
    headerRow: headerIndex + 1,
  };
};

export const extractInhabilitadoSheet = (
  workbook: XLSX.WorkBook,
  sheetName: string
): InhabilitadoSheetData => extractSheetAtHeader(workbook, sheetName);

export const readInhabilitadoWorkbook = async (
  file: File
): Promise<XLSX.WorkBook> => {
  const buffer = await file.arrayBuffer();
  return XLSX.read(buffer, { type: "array", cellDates: true });
};

export const suggestInhabilitadoMapping = (
  headers: string[]
): Record<string, string> => {
  const suggestions: Record<string, string> = {};
  const used = new Set<string>();
  INHABILITADO_SYSTEM_COLUMNS.forEach((column) => {
    const aliases = new Set(
      [column.label, ...column.aliases].map(normalizeInhabilitadoHeader)
    );
    const source = headers.find(
      (header) =>
        !used.has(header) && aliases.has(normalizeInhabilitadoHeader(header))
    );
    if (source) {
      suggestions[column.key] = source;
      used.add(source);
    }
  });
  return suggestions;
};

/** Limpia y deja sólo dígitos y K (mayúscula), con guión antes del DV. */
export const normalizeRutValue = (raw: unknown): string => {
  if (raw === null || raw === undefined) return "";
  const clean = String(raw).toUpperCase().replace(/[^0-9K]/g, "");
  if (clean.length < 2) return "";
  return `${clean.slice(0, -1)}-${clean.slice(-1)}`;
};
